import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoginArea } from "@/components/auth/LoginArea";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useNostrPublish } from "@/hooks/useNostrPublish";
import { useToast } from "@/hooks/useToast";
import { BchLogo } from "../components/BchLogo";
import { AnimatedNumber } from "../components/AnimatedNumber";
import { Confetti } from "../components/Confetti";
import { track } from "@/lib/track";
import { shareToX } from "../share";
import { bchCounterfactualNet, formatFee, formatUsd, methodLabel } from "./engine";
import type { HustleResult } from "./types";

interface HustleResultsProps {
  result: HustleResult;
  onRestart: () => void;
}

const SHARE_URL = "https://surviveonbch.com";

export function HustleResults({ result, onRestart }: HustleResultsProps) {
  const { user } = useCurrentUser();
  const { mutate: publish, isPending } = useNostrPublish();
  const { toast } = useToast();

  const bchNet = bchCounterfactualNet(result);
  const lostToFriction = Math.max(0, Math.round((bchNet - result.netUsd) * 100) / 100);
  const goodDay = result.score >= 60;

  const shareText = buildHustleShare(result, lostToFriction);

  function handleNostrShare() {
    publish(
      {
        kind: 1,
        content: `${shareText}\n\nPlay: ${SHARE_URL}`,
        tags: [
          ["t", "BitcoinCash"],
          ["t", "BCH"],
        ],
      },
      {
        onSuccess: () => {
          track("share_nostr");
          toast({ title: "Shared to Nostr!", description: "Your hustle is on Nostr. 🟢" });
        },
        onError: () =>
          toast({
            title: "Couldn't publish",
            description: "Something went wrong sharing to Nostr.",
            variant: "destructive",
          }),
      },
    );
  }

  return (
    <div className="flex flex-col gap-5 animate-screen-enter">
      <Confetti fire={goodDay || result.bchConverts > 0} />

      {/* Hero */}
      <Card className={goodDay ? "border-primary/60" : "border-destructive/60"}>
        <CardContent className="p-6 text-center flex flex-col items-center gap-3">
          <div className="text-5xl" aria-hidden>{goodDay ? "🏪" : "📉"}</div>
          <h1 className="text-2xl font-black text-foreground">
            {goodDay ? "Great Day at the Shop!" : "Rough Day at the Shop"}
          </h1>
          <p className="text-sm text-muted-foreground">
            You served {result.rounds} customers in <span className="font-semibold">Hustle Mode</span>
          </p>
          <div className="flex items-baseline gap-2">
            <span className="text-5xl font-black text-primary">
              <AnimatedNumber
                value={result.score}
                format={(n) => `${Math.round(n)}`}
                duration={900}
                flash={false}
              />
            </span>
            <span className="text-sm text-muted-foreground">/ 100 score</span>
          </div>
        </CardContent>
      </Card>

      {/* BCH converts */}
      <Card className="bg-primary/5 border-primary/40">
        <CardContent className="p-5 flex items-center gap-4">
          <div className="text-4xl shrink-0" aria-hidden>
            {result.bchConverts > 0 ? "🎉" : "🌱"}
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-primary">{result.bchConverts}</span>
              <span className="text-sm font-semibold text-foreground">
                new BCH {result.bchConverts === 1 ? "convert" : "converts"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
              {result.bchConverts > 0
                ? `You steered ${result.bchConverts} ${
                    result.bchConverts === 1 ? "customer" : "customers"
                  } who wanted card or BTC into paying in BCH — instant, final, near-zero fee. That's how P2P cash spreads. 🟢`
                : "No conversions this round. Next time, try suggesting BCH to open-minded customers reaching for a card — they'll love how fast and cheap it is."}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stat grid */}
      <div className="grid grid-cols-2 gap-3">
        <Stat label="BCH converts" value={`${result.bchConverts}`} accent />
        <Stat label="Net take-home" value={formatUsd(result.netUsd)} accent />
        <Stat label="Gross sales" value={formatUsd(result.grossUsd)} />
        <Stat
          label="Lost to fees"
          value={formatFee(result.feesUsd)}
          sub={result.spikeFeesUsd > 0 ? `${formatUsd(result.spikeFeesUsd)} from spikes` : undefined}
        />
        <Stat label="Lost to chargebacks" value={formatUsd(result.chargebacksUsd)} />
        <Stat label="Sales completed" value={`${result.completedSales}/${result.rounds}`} />
        <Stat label="Reputation" value={`${Math.round(result.reputation)}%`} accent />
      </div>

      {/* Counterfactual */}
      <Card className="border-primary/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">If you'd taken every sale in BCH…</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="rounded-xl bg-muted/60 px-3 py-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">You kept</p>
              <p className="text-xl font-bold text-foreground">{formatUsd(result.netUsd)}</p>
            </div>
            <div className="rounded-xl bg-primary/10 px-3 py-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">BCH-only</p>
              <p className="text-xl font-bold text-primary">{formatUsd(bchNet)}</p>
            </div>
          </div>
          {lostToFriction > 0.5 ? (
            <p className="text-xs text-center bg-primary/10 text-primary rounded-lg px-3 py-2 font-medium">
              💡 Fees and chargebacks cost you ~{formatUsd(lostToFriction)} today. Accepting BCH —
              final, instant, near-zero fee — would have kept nearly all of it.
            </p>
          ) : (
            <p className="text-xs text-center text-muted-foreground">
              You leaned on cheap, final payments — your take-home was already close to the BCH ideal. Nicely played.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Sales log */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Your sales</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {result.sales.map((s, i) => (
            <div key={i} className="flex items-center justify-between text-sm border-b border-border/60 pb-2 last:border-0 last:pb-0">
              <div className="min-w-0">
                <p className="font-medium text-foreground truncate">
                  {s.customerName} · {s.item}
                </p>
                <p className="text-xs text-muted-foreground">
                  {s.method === "decline"
                    ? "Turned away"
                    : s.completed
                      ? `Took ${methodLabel(s.method)}`
                      : `${methodLabel(s.method)} failed`}
                  {s.chargebackUsd > 0 ? " · charged back" : ""}
                </p>
              </div>
              <div className="text-right shrink-0 ml-2">
                <p className={`font-semibold ${s.netUsd > 0 ? "text-primary" : "text-muted-foreground"}`}>
                  {s.netUsd > 0 ? `+${formatUsd(s.netUsd)}` : "$0.00"}
                </p>
                {(s.feeUsd > 0 || s.chargebackUsd > 0) && (
                  <p className={`text-[11px] ${s.feeSpiked || s.chargebackUsd > 0 ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                    {s.chargebackUsd > 0
                      ? `−${formatUsd(s.chargebackUsd)} clawed`
                      : `−${formatFee(s.feeUsd)} fee${s.feeSpiked ? " ⚠️" : ""}`}
                  </p>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Sharing */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Share your day</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {user ? (
            <Button className="w-full h-12 font-bold" onClick={handleNostrShare} disabled={isPending}>
              {isPending ? "Publishing…" : "Share to Nostr 🟣"}
            </Button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Log in with Nostr to share your day.</p>
              <LoginArea className="w-full" />
            </div>
          )}
          <Button
            variant="secondary"
            className="w-full h-12 font-bold"
            onClick={() => {
              track("share_x");
              shareToX(shareText, SHARE_URL);
            }}
          >
            Share to X 𝕏
          </Button>
        </CardContent>
      </Card>

      {/* BCHNostr CTA */}
      <Card className="bg-primary/5 border-primary/30">
        <CardContent className="p-5 flex flex-col items-center text-center gap-3">
          <BchLogo className="w-12 h-12 text-2xl" />
          <p className="text-sm font-semibold text-foreground">Accept BCH at your real business</p>
          <p className="text-xs text-muted-foreground">
            Near-zero fees, instant settlement, no chargebacks. Explore point-of-sale tools built
            for peer-to-peer digital cash.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <Button asChild variant="outline" className="border-primary text-primary flex-1">
              <a href="https://bchpay.cash" target="_blank" rel="noopener noreferrer">
                BCHpay.cash →
              </a>
            </Button>
            <Button asChild variant="outline" className="border-primary text-primary flex-1">
              <a href="https://bchnostr.com" target="_blank" rel="noopener noreferrer">
                Join BCHNostr →
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Button variant="ghost" className="w-full" onClick={onRestart}>
        ↻ Open Shop Again
      </Button>

      <p className="text-[11px] text-center text-muted-foreground pb-2">
        All values in this game are simulated. No real money or crypto was used.
      </p>
    </div>
  );
}

function buildHustleShare(result: HustleResult, lostToFriction: number): string {
  const lines = [
    `I ran a shop in "Survive on BCH" Hustle Mode! 🏪`,
    `Served ${result.rounds} customers · Net take-home: ${formatUsd(result.netUsd)} · Score: ${result.score}`,
    result.bchConverts > 0
      ? `Converted ${result.bchConverts} ${
          result.bchConverts === 1 ? "customer" : "customers"
        } to BCH along the way. 🟢🌱`
      : "",
    result.chargebacksUsd > 0
      ? `Card chargebacks clawed back ${formatUsd(result.chargebacksUsd)} — BCH sales are final. 🟢`
      : "",
    lostToFriction > 0.5
      ? `Fees + chargebacks cost ~${formatUsd(lostToFriction)}. Accepting BCH would've kept nearly all of it.`
      : "",
    `(All simulated — no real money.)`,
    `#BitcoinCash #BCH`,
  ].filter(Boolean);
  return lines.join("\n");
}

function Stat({
  label,
  value,
  accent,
  sub,
}: {
  label: string;
  value: string;
  accent?: boolean;
  sub?: string;
}) {
  return (
    <div className="rounded-xl bg-card border border-border px-4 py-3">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold ${accent ? "text-primary" : "text-foreground"}`}>{value}</p>
      {sub && <p className="text-[10px] text-destructive font-medium mt-0.5">{sub}</p>}
    </div>
  );
}
